import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Chip,
  toast,
} from "@heroui/react";
import { ErrorPage, ErrorState, SkeletonDetail } from "@branding/ui";
import { mutate as globalMutate } from "swr";
import useSWR from "swr";
import { JsonLd } from "../components/JsonLd";
import { SkuPicker } from "../components/SkuPicker";
import { addCartLine, fetchProduct } from "../lib/api";
import { displaySku } from "../lib/display-sku";
import { formatIdr, publicOrigin } from "../lib/format";
import { productJsonLd } from "../lib/json-ld";
import { useSsrData } from "../lib/ssr-data";
import type { Product } from "../lib/types";
import { ApiClientError } from "../lib/v2";

export function ProductPage() {
  const { slug = "" } = useParams();
  const ssr = useSsrData();
  const fallback =
    ssr?.kind === "product" && ssr.product?.slug === slug
      ? ssr.product
      : undefined;
  const { data, error, isLoading, mutate } = useSWR<Product>(
    slug ? `produk:${slug}` : null,
    () => fetchProduct(slug),
    { fallbackData: fallback },
  );
  const initialSku = data ? displaySku(data) : null;
  const [skuId, setSkuId] = useState(initialSku?.id ?? "");
  useEffect(() => {
    if (!skuId && initialSku?.id) setSkuId(initialSku.id);
  }, [initialSku?.id, skuId]);
  const selected = useMemo(() => {
    if (!data) return null;
    return data.skus.find((sku) => sku.id === skuId) ?? displaySku(data);
  }, [data, skuId]);

  if (error instanceof ApiClientError && error.status === 404) {
    return <ErrorPage code={404} actionHref="/pasar" />;
  }
  if (error) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <ErrorState
          title="Produk tidak bisa dibuka"
          description={error instanceof ApiClientError ? error.traceId : undefined}
          onRetry={() => {
            void mutate();
          }}
        />
      </main>
    );
  }
  if (isLoading && !data) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <SkeletonDetail />
      </main>
    );
  }
  if (!data || !selected) return <ErrorPage code={404} actionHref="/pasar" />;

  const inStock = selected.available > 0;
  const activeSkuId = skuId || selected.id;

  async function onAdd() {
    if (!inStock) return;
    try {
      await addCartLine(activeSkuId, 1);
      await globalMutate("cart");
      toast.success("Masuk keranjang");
    } catch (caught) {
      const clientError = caught instanceof ApiClientError ? caught : null;
      toast.danger(clientError?.title ?? "Tidak bisa menambah ke keranjang");
    }
  }

  return (
    <main className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-10 md:grid-cols-[1.1fr_0.9fr] md:px-6">
      <JsonLd data={productJsonLd(data, publicOrigin())} />
      <div className="pasar-stall overflow-hidden rounded-3xl">
        <div className="pasar-weave min-h-72 md:min-h-[28rem]" aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-5">
        <nav className="text-sm text-muted" aria-label="Jejak">
          <a href="/pasar">Pasar Kupang</a>
          {data.category ? (
            <>
              {" / "}
              <a href={`/pasar/kategori/${data.category.slug}`}>
                {data.category.name}
              </a>
            </>
          ) : null}
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
          {data.name}
        </h1>
        <p className="text-sm">
          Toko{" "}
          <a href={`/pasar/toko/${data.merchant.slug}`}>{data.merchant.name}</a>
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <p className="pasar-price text-3xl font-semibold">
            {formatIdr(selected.price)}
          </p>
          <Chip color={inStock ? "success" : "danger"} variant="soft">
            <Chip.Label>{inStock ? "Tersedia" : "Habis"}</Chip.Label>
          </Chip>
        </div>
        <SkuPicker
          skus={data.skus}
          value={activeSkuId}
          onChange={setSkuId}
        />
        {data.description ? (
          <p className="max-w-prose text-base leading-relaxed text-muted">
            {data.description}
          </p>
        ) : null}
        <div>
          <Button
            variant="primary"
            isDisabled={!inStock}
            onPress={() => {
              void onAdd();
            }}
          >
            {inStock ? "Masukkan keranjang" : "Stok habis"}
          </Button>
        </div>
      </div>
    </main>
  );
}
